import type { PickerItemValidCheck } from "@9amhealth/shared";
import styled from "@emotion/styled";
import { getLocalTimeZone, now, ZonedDateTime } from "@internationalized/date";
import React, { FC, useRef } from "react";
import { Dialog, Modal as RAModal, ModalOverlay } from "react-aria-components";
import { TimePickerSheet } from "../TimePickerSheet/TimePickerSheet";

// react-aria primitives don't impose visual styling — TimePickerSheet provides
// its own backdrop/sheet chrome. These wrappers exist purely so the picker
// joins react-aria's overlay stack and isn't inerted by an open Modal.
const Overlay = styled(ModalOverlay)`
  display: contents;
`;

const Panel = styled(RAModal)`
  display: contents;
`;

const StyledDialog = styled(Dialog)`
  display: contents;
  outline: none;
`;

const DateTimePicker: FC<{
  show: boolean;
  onFinish?: (dateTime: ZonedDateTime) => void;
  onChange?: (dateTime: ZonedDateTime) => void;
  initialDate?: ZonedDateTime;
  title: string;
  granularity: "day" | "minute";
  daysBack?: number;
  daysForward?: number;
  validateDate?: PickerItemValidCheck;
}> = (props) => {
  const {
    show,
    onFinish,
    onChange,
    initialDate = now(getLocalTimeZone()),
    title,
    granularity,
    daysBack,
    daysForward,
    validateDate
  } = props;
  const selected = useRef<ZonedDateTime>(initialDate);

  const handleFinish = () => {
    onFinish?.(selected.current);
  };

  const handleSelectChange = (dateTime: ZonedDateTime) => {
    selected.current = dateTime;
    onChange?.(dateTime);
  };

  if (!show) {
    return null;
  }

  return (
    <Overlay isOpen isDismissable={false} isKeyboardDismissDisabled>
      <Panel>
        <StyledDialog aria-label={title}>
          <TimePickerSheet
            onFinish={handleFinish}
            onChange={handleSelectChange}
            title={title}
            granularity={granularity}
            initialDate={initialDate}
            daysBack={daysBack}
            daysForward={daysForward}
            validateDate={validateDate}
          />
        </StyledDialog>
      </Panel>
    </Overlay>
  );
};

export default DateTimePicker;
